import type { IncludedFuneralHome, Lead } from '@/apiTypes'
import Plan from '@/classes/Plan'
import type { SessionImport } from '@/classes/importers/AppointmentImporter'
import AppointmentImporter from '@/classes/importers/AppointmentImporter'
import type { PlanData, SessionData } from '@/types'

/** Class to import data from an appointment into the session builder */
export default class CrmAppointmentImporter extends AppointmentImporter {
	private appointmentId: string

	public constructor(appointmentId: string) {
		super()
		this.appointmentId = appointmentId
	}

	public async import(): Promise<SessionImport> {
		const sessionData: Partial<SessionData> = {}
		const plans: Partial<PlanData>[] = []

		const appt = await this.getAppointmentById(this.appointmentId)

		/** Session-wide data */
		sessionData.appointmentId = appt.id

		if (appt.funeralHome?.data.groupId) {
			sessionData.crmFhGroupId = appt.funeralHome.data.groupId
		}

		if (appt.funeralHome?.data.salesforceId) {
			sessionData.FuneralHomeLocationId = appt.funeralHome.data.salesforceId
		}

		/** TODO: find and reuse existing plans */

		/** Plan data */
		const lead = appt.lead?.data

		if (!lead) {
			console.error('Loaded appointment has no lead')
			return { session: sessionData, plans, planPrototype: {} }
		}

		// if prototype were to contain more than leadId, we need to update getPlansFromLead
		const planPrototype: Partial<PlanData> = {
			leadId: lead.id!,
		}

		plans.push(...this.getPlansFromLead(lead))

		return { session: sessionData, plans, planPrototype }
	}

	public getPlansFromLead(lead: Lead & IncludedFuneralHome): Plan[] {
		const contacts = this.getContactsFromLead(lead)

		return contacts.map<Plan>(
			(contact) => new Plan(contact as Partial<PlanData>)
		)
	}

	public getContactsFromLead(lead: Lead & IncludedFuneralHome) {
		// because CRM leads contain multiple people, we split.
		const firstNames = lead.firstname
			.replace(' And ', ' & ')
			.replace(' and ', ' & ')
			.split(' & ')

		// create a full contact from each firstname
		return firstNames.map((firstName) => ({
			firstName: firstName.trim(),
			lastName: lead.lastname,
			address: lead.address,
			city: lead.city,
			state: lead.state,
			zip: lead.zip,
			leadId: lead.id,
			email: lead.email,
			phone: lead.primaryPhone,
		}))
	}
}
